import React from 'react';
import { FaqMain } from '../../../../types/puppyGuide';

export const faq: FaqMain = {
  title: 'Vets Want You to Know',
  faqs: [
    {
      title: 'Schedule your puppy’s first vet visit.',
      answer: (
        <React.Fragment>
          <p>
            You are your puppy’s best health advocate. By scheduling a vet visit within the first
            few days of bringing your puppy home, you are setting them up with the veterinary care
            and routines they need to thrive.
          </p>
          <p>
            Healthy puppies must start on their preventative care early so that they can stay
            healthy, and sick puppies need treatment before their condition becomes serious or even
            life-threatening.
          </p>
          <p>
            Your veterinarian will work with you to ensure your puppy is getting the care and
            support they need to grow into healthy and happy adult dogs.
          </p>
          <a
            target="_blank"
            rel="noreferrer"
            href="https://be.chewy.com/what-to-expect-at-puppys-first-vet-visit/"
          >
            Puppy’s First Vet Visit: What to Expect, Checklist and Tips
          </a>
        </React.Fragment>
      ),
    },
    {
      title: 'Here’s why all dogs need vaccines.',
      answer: (
        <React.Fragment>
          <p>
            Dog vaccinations are critical to ensure the health and longevity of young puppies as
            they grow into adult dogs and become seniors. Vaccines are the safest and most cost-
            effective way to protect your dog from infectious preventable diseases.
          </p>
          <p>
            The science behind canine vaccinations has progressed significantly over the past
            decade, enhancing both their safety and efficacy against existing and emerging
            pathogens.
          </p>
          Every dog should be vaccinated against:
          <ul>
            <li>
              Rabies – considered a core vaccine when required by law or wherever rabies is present
            </li>
            <li>Canine Distemper Virus (CFV)</li>
            <li>Canine Parvovirus (CPV or parvo)</li>
            <li>Canine Adenovirus-2 (CAV-2) – infectious hepatitis</li>
          </ul>
          To learn more about vaccine schedules and available lifestyle vaccines, check out these
          guides:
          <br />
          <a
            target="_blank"
            rel="noreferrer"
            href="https://be.chewy.com/dog-vaccinations-a-complete-guide/"
          >
            Dog Vaccinations: A Complete Guide
          </a>
        </React.Fragment>
      ),
    },
    {
      title: 'Start a daily dental care routine.',
      answer: (
        <React.Fragment>
          <p>
            Dental diseases in dogs are quite common. By age 3, over 80% of dogs have some form of
            dental disease, also known as&nbsp;
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.petmd.com/dog/conditions/mouth/c_multi_periodontal_disease"
            >
              periodontal disease
            </a>
            . Dog dental disease has serious consequences, so maintaining good dog dental care is
            very important.
          </p>
          There are a variety of ways you can incorporate dental care into your puppy’s daily
          routine, including:
          <ul>
            <li>Brushing their teeth daily or every other day</li>
            <li>Adding dental powder to their meals</li>
            <li>Adding a water additive to their water bowl daily</li>
            <li>Giving your dog veterinary-approved dental treats</li>
          </ul>
          <p>
            Talk with your vet to learn about which options make the most sense for your puppy and
            lifestyle.
          </p>
          <p>
            Learn more about why dental care is important and dental care tips for dogs:
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.petmd.com/news/view/5-reasons-why-dog-dental-care-important-38003"
            >
              5 Reasons Why Dog Dental Care Is Important
            </a>
          </p>
        </React.Fragment>
      ),
    },
    {
      title: 'Chat with your vet about the right time to spay/neuter your puppy.',
      answer: (
        <React.Fragment>
          <p>
            Every dog is unique and will have their own personalized veterinary care plan. Part of
            this plan is determining the right time to spay or neuter your puppy.
          </p>
          <p>
            The right age for spaying or neutering is going to vary depending on your dog’s gender,
            size, breed, and other factors. Your vet will work with you to figure out what makes the
            most sense for you and your puppy.
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.petmd.com/dog/care/spay-and-neutering-dogs-101-everything-you-need-know"
            >
              Spaying and Neutering Dogs 101: Everything You Need to Know
            </a>
          </p>
        </React.Fragment>
      ),
    },
    {
      title: 'Microchips can help locate lost dogs. ',
      answer: (
        <React.Fragment>
          <p>
            A microchip provides permanent identification for your pet that links them to you, no
            matter where they end up. If your dog is lost, any shelter or veterinarian can scan your
            pet’s microchip to find out your contact information to reunite you as soon as possible.
          </p>
          <p>
            <strong>Remember</strong>: It’s crucial that once you microchip your pet, you enter your
            contact info on the microchip company’s website, or over the phone. You should also keep
            it updated to your most current address.
          </p>
          <p>
            Tip: Your puppy can get microchipped during their spay/neuter surgery while they are
            sedated.
          </p>
          <p>
            Here is a full guide with everything you need to know about pet microchips:
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.petmd.com/dog/pet-microchipping-faq"
            >
              Dog Microchip FAQ: Cost, Where to Go and, How It Works
            </a>
          </p>
        </React.Fragment>
      ),
    },
    {
      title: 'Flea, tick, and heartworm prevention are necessities.',
      answer: (
        <React.Fragment>
          <p>
            Pests are a fact of life. Regardless of your puppy’s lifestyle, it is inevitable that
            they will encounter fleas, ticks, and mosquitoes—just think about how these parasites
            find their way into your home no matter how careful you are.
          </p>
          <p>
            That’s why prevention is paramount. By talking with your vet and starting your puppy on
            flea, tick, and heartworm preventatives when they are ready, you can protect your pet,
            yourself, and your home from infestations, diseases, and the headaches of treatment.
          </p>
          <p>
            Learn about other flea and tick myths that you should ignore:
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://be.chewy.com/busting-myths-about-tick-and-flea-season/"
            >
              5 Super-Common Flea and Tick Myths—And Why You’ve Got to Stop Believing Them
            </a>
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.petmd.com/dog/parasites/what-are-options-heartworm-prevention-dogs"
            >
              What Are the Options for Heartworm Prevention for Dogs?
            </a>
          </p>
        </React.Fragment>
      ),
    },
  ],
};
